/**
 * Determines if a URL is a BookVisit link.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} True if the URL is a BookVisit link, false otherwise.
 *
 * @example
 * isBookVisitLink("https://svalbardadventures.com/en/accommodation/https-online-bookvisit-com-accommodation-room-group-infochannelid671b3392-484a-4b34-b3c2-00a072062e52langid5roomtypegroupid4150022d-6c7a-4747-8ff3-f7780bac5221");
 * // returns true
 * isBookVisitLink("https://svalbardadventures.com/en/accommodation/other-link");
 * // returns false
 */
export const isBookVisitLink = (url) => {
  const lastPath = getLastPathSegment(url);
  const isBookVisit = lastPath.startsWith("https-booking-svalbardadventures-com");
  return isBookVisit;
};

/**
 * Generates a BookVisit URL based on the provided URL.
 *
 * @param {string} url - The URL to generate the BookVisit URL from.
 * @returns {string|null} The generated BookVisit URL or null if the input URL is not valid.
 *
 *
 */
export function generateBookVisitUrl(url) {
  const parsedUrl = parseBookVisitUrl(url);

  if (parsedUrl) {
    const { langId, roomTypeGroupId, base } = parsedUrl;
    const createdUrl = createBookVisitUrl(langId, roomTypeGroupId, base);
    return createdUrl;
  }
  return null;
}

// Regex to extract BookVisit URL parameters
const langIdRegex = /langid(\d+)/;
const roomTypeGroupIdRegex = /roomtypegroupid([0-9a-f-]+)/;

/**
 * Parses a BookVisit URL and extracts the channelId, langId, and roomTypeGroupId.
 *
 * @param {string} url - The BookVisit URL to parse.
 * @returns {Object|null} An object containing channelId, langId, and roomTypeGroupId, or null if the URL is not valid.
 */
const parseBookVisitUrl = (url) => {
  const lastPath = getLastPathSegment(url);

  const base = lastPath.replace("https-booking-svalbardadventures-com", "");

  const langIdMatch = langIdRegex.exec(base);
  const roomTypeGroupIdMatch = roomTypeGroupIdRegex.exec(base);
  // If neither langId nor roomTypeGroupId is found, the URL is considered invalid
  // because these parameters are required to construct a valid BookVisit URL.
  if (!langIdMatch && !roomTypeGroupIdMatch) {
    return null;
  }

  return {
    base,
    langId: langIdMatch ? langIdMatch[1] : null,
    roomTypeGroupId: roomTypeGroupIdMatch ? roomTypeGroupIdMatch[1] : null,
  };
};

function createBookVisitUrl(langId, roomTypeGroupId, base) {
  if (isAccommodationLink(base)) {
    if (hasRoomGroupInfo(base)) {
      return `https://booking.svalbardadventures.com/accommodation/room-group-info?langid=${langId}&roomtypegroupid=${roomTypeGroupId}`;
    }

    return `https://booking.svalbardadventures.com/accommodation?langid=${langId}&roomtypegroupid=${roomTypeGroupId}`;
  }
  if (isConferenceLink(base)) {
    return `https://booking.svalbardadventures.com/conference?langid=${langId}&roomtypegroupid=${roomTypeGroupId}`;
  }

  if (isVoucherLink(base)) {
    return `https://booking.svalbardadventures.com/voucher?langid=${langId}`;
  }

  return `https://booking.svalbardadventures.com/accommodation/room-group-info?langid=${langId}&roomtypegroupid=${roomTypeGroupId}`;
}

/**
 *
 * @param {string} url
 * @returns {string} The last segment of the URL path.
 */
function getLastPathSegment(url) {
  // remove trailing slash
  url = url.replace(/\/$/, "");
  const paths = url.split("/");
  const lastPath = paths[paths.length - 1];
  return lastPath;
}

function isAccommodationLink(url) {
  const lastPath = getLastPathSegment(url);
  return lastPath.includes("accommodation");
}

function isConferenceLink(url) {
  const lastPath = getLastPathSegment(url);
  return lastPath.includes("conference");
}

function hasRoomGroupInfo(url) {
  const lastPath = getLastPathSegment(url);
  return lastPath.includes("room-group-info");
}

function isVoucherLink(url) {
  const lastPath = getLastPathSegment(url);
  return lastPath.includes("voucher");
}
